import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { guessCarrier as guessShape } from '../lib/carrierGuess'; // @@CLEANUP_v90@@

const LABELS = {
  submitted: 'Submitted', pre_production: 'Pre-Production', waiting_on_art: 'Waiting on Art',
  waiting_on_blanks: 'Waiting on Blanks', production: 'In Production', ready_to_ship: 'Ready to Ship',
  completed: 'Completed',
};
const REFRESH_MS = 5 * 60 * 1000; // 5 minutes
// @@PUB_OUTBOUND_TRACK_v1@@ outbound carrier status labels.
const SHIP_STATUS = {
  label_created: 'Label created', in_transit: 'In transit', out_for_delivery: 'Out for delivery',
  ready_for_pickup: 'Ready for pickup', delivered: 'Delivered', exception: 'Exception',
};

// Stage colors (mirror the internal hq-stage-* palette) for calendar chips.
const STAGE_COLORS = {
  submitted: { bg: 'rgba(110,168,255,.14)', fg: '#6ea8ff', bd: 'rgba(110,168,255,.3)' },
  pre_production: { bg: 'rgba(180,140,255,.14)', fg: '#b48cff', bd: 'rgba(180,140,255,.3)' },
  waiting_on_art: { bg: 'rgba(232,193,90,.14)', fg: '#e8c15a', bd: 'rgba(232,193,90,.3)' },
  waiting_on_blanks: { bg: 'rgba(232,150,90,.14)', fg: '#e8965a', bd: 'rgba(232,150,90,.3)' },
  production: { bg: 'rgba(90,200,232,.14)', fg: '#5ac8e8', bd: 'rgba(90,200,232,.3)' },
  ready_to_ship: { bg: 'rgba(80,220,140,.14)', fg: '#5fdc97', bd: 'rgba(80,220,140,.3)' },
  completed: { bg: 'rgba(138,138,144,.14)', fg: '#b6b6bc', bd: 'rgba(138,138,144,.3)' },
};
// Local-date YYYY-MM-DD (no toISOString/UTC shift — same fix as the internal
// calendar). For job dates we slice the incoming ISO string's date part.
function pubLocalYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Public carrier tracking-page URL by tracking number. 'other' -> null
// (rendered as plain text). Number is URL-encoded.
function carrierTrackUrl(carrier, num) {
  const n = encodeURIComponent(String(num || '').trim());
  if (!n) return null;
  switch (carrier) {
    case 'fedex': return `https://www.fedex.com/fedextrack/?trknbr=${n}`;
    case 'ups': return `https://www.ups.com/track?tracknum=${n}`;
    case 'usps': return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`;
    default: return null;
  }
}

// Best-effort carrier guess for OUTBOUND shipments, which only carry a free-text
// option label (no structured carrier field). Strategy, most reliable first:
//   1) tracking-number SHAPE  — UPS '1Z...', USPS 20-22 digits, FedEx 12/15 digits
//   2) label-text sniff       — 'ups' / 'usps' / 'fedex' substring
//   3) give up -> null (render plain text). A wrong link is worse than none.
function guessCarrierFromTracking(num, label) {
  const byShape = guessShape(num); // @@CLEANUP_v90@@ shared, conservative guesser
  if (byShape) return byShape;
  const l = String(label || '').toLowerCase();
  if (l.includes('usps')) return 'usps';
  if (l.includes('ups')) return 'ups';
  if (l.includes('fedex') || l.includes('fed ex') || l.includes('fdx')) return 'fedex';
  return null;
}

// Standalone styles — this page is OUTSIDE the app shell (no sidebar/theme import).
const C = {
  page: { minHeight: '100vh', background: '#0a0a0b', color: '#f4f4f5',
    fontFamily: 'Archivo, system-ui, -apple-system, sans-serif', padding: '32px 40px' },
  h1: { fontFamily: 'Archivo, sans-serif', fontWeight: 800, fontSize: 28, margin: '0 0 4px' },
  sub: { color: '#8a8a90', margin: '0 0 28px', fontSize: 14 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 10, alignItems: 'start' },
  col: { background: '#17171a', border: '1px solid rgba(255,255,255,.08)', borderRadius: 10, padding: 8, minHeight: 80 },
  colHead: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: '#8a8a90', padding: '4px 4px 10px' },
  card: { background: '#121214', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, padding: 10, marginBottom: 8, cursor: 'pointer' },
  poNum: { fontWeight: 700, fontSize: 13 },
  foot: { marginTop: 28, color: '#55555c', fontSize: 12, textAlign: 'center' },
  header: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid rgba(255,255,255,.08)' },
  logo: { height: 40, width: 'auto', display: 'block' },
  headTitles: { display: 'flex', flexDirection: 'column', gap: 2 },
  toggle: { display: 'inline-flex', gap: 6, marginBottom: 20 },
  tBtn: { height: 34, padding: '0 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,.16)', background: 'transparent', color: '#f4f4f5', cursor: 'pointer', fontSize: 13 },
  tBtnOn: { background: '#fff', color: '#0a0a0b', fontWeight: 700 },
  calHead: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 14 },
  calGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 },
  calDow: { textAlign: 'center', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: '#8a8a90', paddingBottom: 4 },
  calCell: { minHeight: 88, background: '#17171a', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, padding: 6, overflow: 'hidden' },
  calToday: { background: 'rgba(110,168,255,.08)', borderColor: 'rgba(110,168,255,.45)' },
  calEmpty: { background: 'transparent', border: '1px solid transparent' },
  calDay: { fontSize: 12, color: '#8a8a90', marginBottom: 4 },
  calJob: { fontSize: 11, padding: '3px 6px', borderRadius: 5, marginBottom: 4, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', border: '1px solid' },
};

export default function PublicProduction() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState(null); // selected job detail
  const [view, setView] = useState('board'); // 'board' (default) | 'calendar'
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/pub-api/vendor/${token}`);
      if (res.status === 404) { setErr('This link is no longer active.'); setData(null); return; }
      if (!res.ok) { setErr('Could not load.'); return; }
      const d = await res.json();
      setData(d); setErr(''); setLastRefresh(new Date());
    } catch (e) { setErr('Could not reach the server.'); }
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  // @@SITE_ANALYTICS_v92@@ one page view per page load (the auto-refresh above is
  // not a view). Sends only the referrer + query string — the token is already in
  // the path and is never stored. Best-effort; failures are ignored.
  useEffect(() => {
    try {
      fetch(`/pub-api/vendor/${token}/view`, {
        method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ref: document.referrer || '', search: window.location.search || '' }),
      }).catch(() => {});
    } catch (e) { /* ignore */ }
  }, [token]);

  async function openJob(jobId) {
    try {
      const res = await fetch(`/pub-api/vendor/${token}/job/${jobId}`);
      if (res.ok) { const d = await res.json(); setSel(d.job); }
    } catch (e) { /* ignore */ }
  }

  async function setStatus(jobId, status) {
    await fetch(`/pub-api/vendor/${token}/job/${jobId}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    await openJob(jobId); await load();
  }
  async function addNote(jobId, note) {
    if (!note.trim()) return;
    await fetch(`/pub-api/vendor/${token}/job/${jobId}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    });
    await openJob(jobId); await load();
  }

  if (err) return <div style={C.page}><h1 style={C.h1}>Production</h1><p style={C.sub}>{err}</p></div>;
  if (!data) return <div style={C.page}><p style={C.sub}>Loading…</p></div>;

  const byStage = {};
  data.stages.forEach((st) => { byStage[st] = []; });
  data.jobs.forEach((j) => { (byStage[j.status] = byStage[j.status] || []).push(j); });

  return (
    <div style={C.page}>
      <div style={C.header}>
        <img style={C.logo} src="/brand/xyz-mark.png" alt="XYZ Merchandise" />
        <div style={C.headTitles}>
          <h1 style={C.h1}>{data.vendor.name} — Production</h1>
          <p style={{ ...C.sub, margin: 0 }}>
            Live production status. Tap a job to update it or add a note.
            {lastRefresh ? ` · Updated ${lastRefresh.toLocaleTimeString()}` : ''}
          </p>
        </div>
      </div>

      <div style={C.toggle}>
        <button style={{ ...C.tBtn, ...(view === 'board' ? C.tBtnOn : {}) }} onClick={() => setView('board')}>Board</button>
        <button style={{ ...C.tBtn, ...(view === 'calendar' ? C.tBtnOn : {}) }} onClick={() => setView('calendar')}>Calendar</button>
      </div>

      {view === 'board' ? (
        <div style={C.grid}>
          {data.stages.map((st) => (
            <div key={st} style={C.col}>
              <div style={C.colHead}>{LABELS[st] || st} ({byStage[st].length})</div>
              {byStage[st].map((j) => {
                /* @@PUB_BOARD_COLORS_v57@@ */
                const sc = STAGE_COLORS[j.status] || { bg: C.card.background, fg: '#f4f4f5', bd: 'rgba(255,255,255,.08)' };
                return (
                <div key={j.id} style={{ ...C.card, background: sc.bg, borderColor: sc.bd, borderLeft: `3px solid ${sc.fg}` }} onClick={() => openJob(j.id)}>
                  <div style={C.poNum}>{j.po_number || '—'}</div>
                  {j.job_title && <div style={{ color: '#f4f4f5', fontSize: 12, marginTop: 2 }}>{j.job_title}</div>}
                  {j.ship_by_date && <div style={{ color: '#8a8a90', fontSize: 11, marginTop: 2 }}>Ship by {String(j.ship_by_date).slice(0, 10)}</div>}
                </div>
                );
              })}
            </div>
          ))}
        </div>
      ) : (
        <PubMonth jobs={data.jobs} cursor={cursor} setCursor={setCursor} onOpen={openJob} />
      )}

      {sel && <JobModal job={sel} token={token} stages={data.stages} onClose={() => setSel(null)} onStatus={setStatus} onNote={addNote} />}

      <div style={C.foot}>Auto-refreshes every 5 minutes.</div>
    </div>
  );
}

function PubMonth({ jobs, cursor, setCursor, onOpen }) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Bucket jobs by their ship_by_date (date part of the ISO string).
  const byDate = {};
  jobs.forEach((j) => {
    if (!j.ship_by_date) return;
    const key = String(j.ship_by_date).slice(0, 10);
    (byDate[key] = byDate[key] || []).push(j);
  });
  const undated = jobs.filter((j) => !j.ship_by_date);

  const todayKey = pubLocalYmd(new Date());
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    const key = pubLocalYmd(new Date(year, month, day));
    cells.push({ day, key, jobs: byDate[key] || [] });
  }
  const monthName = first.toLocaleString(undefined, { month: 'long', year: 'numeric' });

  function chip(j) {
    const c = STAGE_COLORS[j.status] || { bg: '#222', fg: '#ccc', bd: '#333' };
    return (
      <div key={j.id} style={{ ...C.calJob, background: c.bg, color: c.fg, borderColor: c.bd }}
        onClick={() => onOpen(j.id)} title={j.job_title ? `${j.po_number} · ${j.job_title}` : j.po_number}>
        {j.po_number || '—'}{j.job_title ? ` · ${j.job_title}` : ''}
      </div>
    );
  }

  return (
    <div>
      {undated.length > 0 && (
        <div style={{ background: '#17171a', border: '1px dashed rgba(255,255,255,.16)', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: '#8a8a90', marginBottom: 8 }}>No ship-by date</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{undated.map(chip)}</div>
        </div>
      )}
      <div data-k="pub-legend" style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid rgba(255,255,255,.08)' }}>
        {Object.keys(STAGE_COLORS).map((st) => {
          const c = STAGE_COLORS[st];
          return (
            <span key={st} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#8a8a90' }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: c.bg, border: `1px solid ${c.bd}`, display: 'inline-block' }} />
              {LABELS[st] || st}
            </span>
          );
        })}
      </div>
      <div style={C.calHead}>
        <button style={C.tBtn} onClick={() => setCursor(new Date(year, month - 1, 1))}>‹</button>
        <h2 style={{ margin: 0, fontSize: 18 }}>{monthName}</h2>
        <button style={C.tBtn} onClick={() => setCursor(new Date(year, month + 1, 1))}>›</button>
      </div>
      <div style={C.calGrid}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d) => (<div key={d} style={C.calDow}>{d}</div>))}
        {cells.map((c, idx) => (
          <div key={idx} style={{ ...C.calCell, ...(c ? {} : C.calEmpty), ...(c && c.key === todayKey ? C.calToday : {}) }}>
            {c && <div style={C.calDay}>{c.day}</div>}
            {c && c.jobs.map(chip)}
          </div>
        ))}
      </div>
    </div>
  );
}

function JobModal({ job, token, stages, onClose, onStatus, onNote }) {
  const [note, setNote] = useState('');
  function printJob() {
    const esc = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const li = (job.line_items || []).map((x) =>
      `<tr><td>${esc(x.name || x.sku || '—')}</td><td>${esc(x.description || '—')}</td><td>${esc(x.quantity != null ? x.quantity : '—')}${x.unit ? ' ' + esc(x.unit) : ''}</td></tr>`
    ).join('') || '<tr><td colspan="3">No items.</td></tr>';
    const sh = (job.shipments || []).map((x) =>
      `<tr><td>${esc(x.label || '—')}</td><td>${esc((x.carrier || '').toUpperCase())}</td><td>${esc(x.tracking_number || '—')}</td><td>${esc(x.status || '—')}</td><td>${esc(x.estimated_delivery ? String(x.estimated_delivery).slice(0,10) : '—')}</td></tr>`
    ).join('') || '<tr><td colspan="5">No inbound shipments.</td></tr>';
    const out = (job.outbound_shipments || []).map((x) =>
      `<tr><td>${esc(x.label || '—')}</td><td>${esc(x.tracking_number || '—')}</td><td>${esc(x.status ? (x.status.replace(/_/g,' ')) : '—')}</td><td>${esc(x.estimated_delivery ? String(x.estimated_delivery).slice(0,10) : '—')}</td><td>${x.has_file ? 'Label attached' : '—'}</td></tr>`
    ).join('') || '<tr><td colspan="5">No outbound shipments.</td></tr>';
    const hist = (job.history || []).map((h) =>
      `<li>${h.from_status ? esc(LABELS[h.from_status] || h.from_status) + ' → ' : ''}${esc(LABELS[h.to_status] || h.to_status)}${h.note ? ' — ' + esc(h.note) : ''}</li>`
    ).join('') || '<li>No history.</li>';
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(job.po_number || 'Job')}</title>
      <style>
        body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 32px; }
        h1 { font-size: 40px; margin: 0 0 6px; text-align: center; letter-spacing: .02em; }
        .statusline { text-align: center; margin-bottom: 8px; }
        h2 { font-size: 13px; text-transform: uppercase; letter-spacing: .06em; color: #555; margin: 22px 0 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
        .status { display: inline-block; padding: 4px 10px; border: 1px solid #111; border-radius: 4px; font-weight: bold; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th { text-align: left; color: #555; border-bottom: 1px solid #ccc; padding: 4px 6px; }
        td { padding: 4px 6px; border-bottom: 1px solid #eee; vertical-align: top; }
        col.c1 { width: 12%; } col.c2 { width: 76%; } col.c3 { width: 12%; }
        ul { margin: 0; padding-left: 18px; font-size: 13px; }
        .print-footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #ddd; text-align: center; }
        .print-footer img { height: 28px; width: auto; }
        .print-footer .wm { font-weight: 800; letter-spacing: .14em; font-size: 13px; color: #111; }
        @media print { body { margin: 12mm; } }
      </style></head><body>
      <h1>${esc(job.po_number || 'Job')}</h1>
      ${job.job_title ? `<div class="statusline">${esc(job.job_title)}</div>` : ''}
      <div class="statusline">Status: <span class="status">${esc(LABELS[job.status] || job.status)}</span></div>
      <h2>What's being produced</h2>
      <table><colgroup><col class="c1"><col class="c2"><col class="c3"></colgroup>
        <thead><tr><th>Item</th><th>Description</th><th>Qty</th></tr></thead><tbody>${li}</tbody></table>
      <h2>Inbound tracking</h2>
      <table><thead><tr><th>Shipment</th><th>Carrier</th><th>Tracking #</th><th>Status</th><th>ETA</th></tr></thead><tbody>${sh}</tbody></table>
      <h2>Outbound shipping</h2>
      <table><thead><tr><th>Method</th><th>Tracking #</th><th>Status</th><th>ETA</th><th>Label</th></tr></thead><tbody>${out}</tbody></table>
      <h2>History</h2>
      <ul>${hist}</ul>
      <div class="print-footer">
        <img src="${window.location.origin}/brand/xyz-mark-dark.png" alt="XYZ Merchandise"
          onerror="this.style.display='none'; var s=document.createElement('div'); s.className='wm'; s.textContent='XYZ MERCHANDISE'; this.parentNode.appendChild(s);" />
      </div>
      <script>window.onload = function(){ window.print(); };<\/script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) return; // popup blocked
    w.document.open(); w.document.write(html); w.document.close();
  }
  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 50 };
  const modal = { background: '#17171a', border: '1px solid rgba(255,255,255,.12)', borderRadius: 14,
    padding: 24, width: 720, maxWidth: '100%', maxHeight: '85vh', overflow: 'auto', color: '#f4f4f5' };
  const btn = { height: 36, padding: '0 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,.16)',
    background: 'transparent', color: '#f4f4f5', cursor: 'pointer', fontSize: 13, marginRight: 6, marginBottom: 6 };
  const input = { width: '100%', minHeight: 60, background: '#1c1c20', color: '#f4f4f5',
    border: '1px solid rgba(255,255,255,.12)', borderRadius: 8, padding: 10, fontFamily: 'inherit', marginTop: 8, boxSizing: 'border-box' };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h2 style={{ marginTop: 0, marginBottom: 0 }}>{job.po_number || ''}</h2>
          <button style={{ ...btn, background: '#fff', color: '#0a0a0b', fontWeight: 700, marginBottom: 0 }} onClick={printJob}>Print</button>
        </div>

        <h3 style={{ fontSize: 13, color: '#8a8a90', textTransform: 'uppercase', letterSpacing: '.06em' }}>Set status</h3>
        <div>
          {stages.map((st) => (
            <button key={st} style={{ ...btn, ...(st === job.status ? { background: '#fff', color: '#0a0a0b', fontWeight: 700 } : {}) }}
              onClick={() => onStatus(job.id, st)}>
              {LABELS[st] || st}
            </button>
          ))}
        </div>

        <h3 style={{ fontSize: 13, color: '#8a8a90', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 18 }}>What's being produced</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 13 }}>
          <colgroup>
            <col style={{ width: '10%' }} />
            <col style={{ width: '80%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead><tr style={{ color: '#8a8a90', textAlign: 'left' }}><th>Item</th><th>Description</th><th>Qty</th></tr></thead>
          <tbody>
            {(job.line_items || []).map((li, idx) => (
              <tr key={idx}><td>{li.name || li.sku || '—'}</td><td style={{ color: '#b6b6bc' }}>{li.description || '—'}</td><td>{li.quantity != null ? li.quantity : '—'}{li.unit ? ` ${li.unit}` : ''}</td></tr>
            ))}
            {(!job.line_items || job.line_items.length === 0) && <tr><td colSpan={3} style={{ color: '#8a8a90' }}>No items.</td></tr>}
          </tbody>
        </table>

        <h3 style={{ fontSize: 13, color: '#8a8a90', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 18 }}>Inbound tracking</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ color: '#8a8a90', textAlign: 'left' }}><th>Shipment</th><th>Carrier</th><th>Tracking #</th><th>Status</th><th>ETA</th></tr></thead>
          <tbody>
            {(job.shipments || []).map((sh, idx) => {
              const url = carrierTrackUrl(sh.carrier, sh.tracking_number);
              return (
                <tr key={idx}>
                  <td>{sh.label || '—'}</td>
                  <td style={{ textTransform: 'uppercase' }}>{sh.carrier}</td>
                  <td>{url
                    ? <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#6ea8ff' }}>{sh.tracking_number}</a>
                    : (sh.tracking_number || '—')}</td>
                  <td>{sh.status || '—'}</td>
                  <td>{sh.estimated_delivery ? String(sh.estimated_delivery).slice(0, 10) : '—'}</td>
                </tr>
              );
            })}
            {(!job.shipments || job.shipments.length === 0) && (
              <tr><td colSpan={5} style={{ color: '#8a8a90' }}>No inbound shipments.</td></tr>
            )}
          </tbody>
        </table>

        <h3 style={{ fontSize: 13, color: '#8a8a90', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 18 }}>Outbound shipping</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ color: '#8a8a90', textAlign: 'left' }}><th>Method</th><th>Tracking #</th><th>Status</th><th>ETA</th><th>Label</th></tr></thead>
          <tbody>
            {(job.outbound_shipments || []).map((sh, idx) => {
              const carrier = guessCarrierFromTracking(sh.tracking_number, sh.label);
              const url = carrier ? carrierTrackUrl(carrier, sh.tracking_number) : null;
              const dl = `/pub-api/vendor/${token}/job/${job.id}/shipment/${sh.id}/label`;
              return (
                <tr key={sh.id || idx}>
                  <td>{sh.label || '—'}</td>
                  <td>{sh.tracking_number
                    ? (url
                        ? <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#6ea8ff' }}>{sh.tracking_number}</a>
                        : sh.tracking_number)
                    : '—'}</td>
                  <td>{sh.status ? (SHIP_STATUS[sh.status] || sh.status) : '—'}</td>
                  <td>{sh.estimated_delivery ? String(sh.estimated_delivery).slice(0, 10) : '—'}</td>
                  <td>{sh.has_file
                    ? <a href={dl} target="_blank" rel="noopener noreferrer" style={{ color: '#6ea8ff' }}>Download</a>
                    : '—'}</td>
                </tr>
              );
            })}
            {(!job.outbound_shipments || job.outbound_shipments.length === 0) && (
              <tr><td colSpan={5} style={{ color: '#8a8a90' }}>No outbound shipments.</td></tr>
            )}
          </tbody>
        </table>

        <h3 style={{ fontSize: 13, color: '#8a8a90', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 18 }}>Add a note</h3>
        <textarea style={input} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for this job…" />
        <div style={{ marginTop: 8 }}>
          <button style={{ ...btn, background: '#fff', color: '#0a0a0b', fontWeight: 700 }}
            onClick={() => { onNote(job.id, note); setNote(''); }}>Add note</button>
          <button style={btn} onClick={onClose}>Close</button>
        </div>

        <h3 style={{ fontSize: 13, color: '#8a8a90', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 18 }}>History</h3>
        <div style={{ fontSize: 12, color: '#b6b6bc' }}>
          {(job.history || []).map((h, idx) => (
            <div key={idx} style={{ padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
              {h.from_status ? `${LABELS[h.from_status] || h.from_status} → ` : ''}{LABELS[h.to_status] || h.to_status}{h.note ? ` — ${h.note}` : ''}
            </div>
          ))}
          {(!job.history || job.history.length === 0) && <div style={{ color: '#8a8a90' }}>No history.</div>}
        </div>
      </div>
    </div>
  );
}
